import Alert from "@mui/material/Alert";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  ResourceView,
  filterToCondition,
  useResourceView,
  type FilterDefinition,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  eventScopeToCondition,
  type EventQueryScope,
} from "@/shared/domain/events/EventQueryScope";
import { EventRecord } from "@/shared/domain/events/EventRecord";
import { eventsTableColumns } from "@/shared/domain/events/EventsTableColumns";
import {
  eventQueryKeys,
  useEventMetadataKeys,
} from "@/shared/domain/events/hooks";
import {
  composeQueryBody,
  formatMetadataValue,
  QueryContentMode,
  QueryTarget,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useEventTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  buildColumnsForPreferences,
  colId,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
} from "@/shared/foundation/primitives/Table";

import { EventDetails } from "./EventDetails";
import styles from "./EventsList.module.css";

const STATIC_EVENT_PICKER_OPTIONS: ColumnPickerOption[] =
  eventsTableColumns.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));

interface EventsListProps {
  scope: EventQueryScope;
  selectionOptions?: SelectionOption<EventRecord>[];
}

export function EventsList({ scope, selectionOptions }: EventsListProps) {
  const { currentOrganization } = useAuth();
  const { events } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useEventTablePreferences();
  const metadataKeysQuery = useEventMetadataKeys(resolvedOrgId);

  // Capture initial columns preferences once at mount
  const [initialColumnIds] = React.useState(() => preferences.columns);

  const columns = React.useMemo<ColDef<EventRecord>[]>(
    () =>
      buildColumnsForPreferences<EventRecord>({
        staticColumns: eventsTableColumns,
        dynamicColumns: (metadataKeysQuery.data ?? []).map<ColDef<EventRecord>>(
          (key) => ({
            colId: `metadata.${key}`,
            headerName: key,
            valueGetter: (p) => p.data?.metadata?.[key],
            valueFormatter: (p) => formatMetadataValue(p.value),
            sortable: false,
          }),
        ),
        preferenceOrder: initialColumnIds,
      }),
    [initialColumnIds, metadataKeysQuery.data],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_EVENT_PICKER_OPTIONS,
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [metadataKeysQuery.data],
  );

  const view = useResourceView<EventRecord>({
    getRowId: (row) => row.event_id,
    initialSort: { columnId: "start_time", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      { type: "string", field: "event_id", name: "Event Id" },
      { type: "string", field: "name", name: "Name" },
      { type: "string", field: "description", name: "Description" },
      { type: "date", field: "created", name: "Created" },
      { type: "string", field: "created_by", name: "Created By" },
      { type: "date", field: "modified", name: "Modified" },
      { type: "string", field: "modified_by", name: "Modified By" },
      {
        type: "enum",
        field: "tags",
        name: "Tags",
        options: function getEventTags() {
          return events.getTagsForOrg({
            resourceOwnerId: resolvedOrgId ?? "",
          });
        },
      },
    ],
    [events, resolvedOrgId],
  );

  const filterCondition =
    view.filters.type === "structured" && view.filters.filters.length > 0
      ? filterToCondition(view.filters.filters, {
          matchMode: view.filters.matchMode,
        })
      : undefined;

  const query = composeQueryBody({
    scopeCondition: eventScopeToCondition(scope),
    filterCondition,
    sort: view.table.sort,
  });

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Events,
    // Events ship with metadata
    contentMode: QueryContentMode.RecordWithMeta,
    queryKey: eventQueryKeys.list(resolvedOrgId, scope, query),
  });
  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<EventRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  const renderExpandedRow = React.useCallback(
    (row: EventRecord) => <EventDetails event={row} readonly={false} />,
    [],
  );

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          <ResourceView.Selection<EventRecord>
            selection={view.table.selection}
            clearSelection={view.table.clearSelection}
            options={selectionOptions ?? []}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<EventRecord>
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No events"
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
          expansion={{ renderRow: renderExpandedRow }}
        />
      </ResourceView.Root>
    </>
  );
}
